import { createRoot } from 'react-dom/client';
// import {
//   ChakraProvider as ThemeProvider,
//   extendTheme,
//   Center,
// } from '@chakra-ui/react';
import { matchPath } from 'react-router-dom';

import './trusted-security-policies';
import './extensions';
import 'simplebar/src/simplebar.css';
import 'react-modern-drawer/dist/index.css';
import './styles/index.css';
// Import Kula components styles
import '@kula-ai/components/style.css';
import { createBrowserHistory } from 'history';

import { App as Core } from './app';
// import { SignUpApp } from '@/signup/app';
// import { AppLoading } from './app/AppLoading';
// import { customTheme } from './theme';
import './utils/yup';

// integrations
import { withLDProvider } from 'launchdarkly-react-client-sdk';
import { PostHogProvider } from 'posthog-js/react';
import posthog from 'posthog-js';
import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { AssessmentApp } from './assessment/app';
import { IntegrationApp } from 'integrations/app';
import { CandidateSurveyApp } from './candidateSurvey/app';
import { CandidateConsentApp } from './candidateConsent/app';

if (import.meta.env.VITE_SENTRY_DSN) {
  const history = createBrowserHistory();

  const routes = [
    { path: '/signup' },
    { path: '/login' },
    { path: '/password_reset' },
    { path: '/intro/addemail' },
    { path: '/intro/consent' },
    { path: '/circles/:companyId' },
    { path: '/circles' },
    { path: '/candidates/:contactId' },
    { path: '/candidates' },
    { path: '/interviews/all' },
    { path: '/interviews/:interviewId' },
    { path: '/settings' },
    { path: '/tasks' },
    { path: '/blueprints/:blueprintId' },
    { path: '/flows/:outreachId' },
    { path: '/flows' },
    { path: '/templates/:templateId/edit' },
    { path: '/templates/:templateId/clone' },
    { path: '/templates/new' },
    { path: '/templates' },
    { path: '/reports-home' },
    { path: '/reports/flow-health' },
    { path: '/reports/diversity' },
    { path: '/reports/kpi' },
    { path: '/reports/team-productivity' },
    { path: '/reports/ats' },
    { path: '/reports' },
    { path: '/projects/:projectId' },
    { path: '/projects' },
    { path: '/projects/reviews' },
    { path: '/rippling' },
    { path: '/' },
  ];

  const EXTENSION_URL_RE =
    /^(chrome-extension|moz-extension|safari-extension|safari-web-extension):\/\//;

  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_ENV,
    integrations: [
      new Integrations.BrowserTracing(),
      new Sentry.Replay(),
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.reactRouterV5Instrumentation(
          history,
          routes,
          matchPath
        ),
        shouldCreateSpanForRequest: (url) =>
          !url.includes('kula-ai-attachments-prod.s3.amazonaws.com/resumes'),
      }),
    ],

    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    tracesSampleRate: 0.1,
    ignoreErrors: [
      /^ChunkLoadError$/,
      '_avast_submit',
      /RenderingCancelledException/,
      /UnknownErrorException.*ensureDoc/,
      /Transport destroyed/,
      /AbortError.*play\(\)/,
      /^LaunchDarklyFlagFetchError/,
      /bufferBelongsToIframe/,
      /ResizeObserver loop/,
      /Non-Error promise rejection captured.*Object Not Found Matching Id/,
    ],
    denyUrls: [EXTENSION_URL_RE],
    beforeSend(event, hint) {
      const error = hint?.originalException as
        | (Error & {
            name?: string;
            message?: string;
            response?: { status?: number };
          })
        | undefined;

      if (error?.name === 'AbortError' || error?.name === 'CanceledError') {
        return null;
      }

      const message = error?.message || event.message || '';
      if (
        /Network Error|Failed to fetch|Load failed|network error/i.test(message)
      ) {
        return null;
      }

      const status = error?.response?.status;
      if (status && status >= 500 && status <= 599) {
        return null;
      }

      const topFrame =
        event.exception?.values?.[0]?.stacktrace?.frames?.slice(-1)[0];
      const topFile = topFrame?.filename || '';
      if (EXTENSION_URL_RE.test(topFile) || topFile.startsWith('blob:')) {
        return null;
      }

      return event;
    },
  });
}

const container = document.getElementById('root');
const root = createRoot(container);

const AppToRender = () => {
  // const { feSignup } = useFlags();
  // const isSignUp = window?.location?.host?.split('.')?.[0] === 'signup';
  const isAssessment = window?.location?.host?.split('.')?.[0] === 'assessment';
  const subdomain = window?.location?.host?.split('.')?.[0];
  //https://integration.kula.ai/rippling
  const isIntegration = subdomain === 'integration';
  const isCandidateSurvey = subdomain === 'survey';
  const isConsent = subdomain === 'consent';
  // const theme = extendTheme({ ...customTheme });

  // if (process.env.REACT_APP_LAUNCHDARKLY && typeof feSignup !== 'boolean') {
  //   return (
  //     <ThemeProvider theme={theme}>
  //       <Center width="100%" height="100%">
  //         <AppLoading />
  //       </Center>
  //     </ThemeProvider>
  //   );
  // }

  // if (isSignUp && feSignup) {
  //   return <SignUpApp />;
  // }

  if (isAssessment) {
    return <AssessmentApp />;
  }
  if (isIntegration) {
    return <IntegrationApp />;
  }
  if (isCandidateSurvey) {
    // TODO: Uncomment this once the candidate survey app is ready
    return <CandidateSurveyApp />;
  }
  if (isConsent) {
    return <CandidateConsentApp />;
  }
  return <Core />;
};

export const AppWithLD = withLDProvider({
  clientSideID: import.meta.env.VITE_LAUNCHDARKLY,
  context: {
    kind: 'user',
    key: `${window.location.host.split('.')[0]}:anonymous`,
  },
  options: {
    streaming: false,
  },
})(AppToRender);

// posthog config
if (import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    loaded: function (ph) {
      // setting posthog on window for accessing posthog toolbar
      Object.assign(window, { posthog: ph });
    },
  });
}

export const App = () => {
  if (import.meta.env.VITE_LAUNCHDARKLY) {
    return <AppWithLD />;
  }
  return (
    <PostHogProvider client={posthog}>
      <AppToRender />
    </PostHogProvider>
  );
};

root.render(<App />);
